/* shared presentational pieces: toasts, badges, bars, sparkline, copy, floating menus */
import { Component, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createStore, hooks, toast } from "../lib/store";
import { lvl, statusClass } from "../lib/fmt";

/* ---------- toasts ---------- */
type T = { id: number; msg: string; sub?: string; kind?: string };
const toasts = createStore<T[]>([]);
let tid = 0;
hooks.toast = (msg, sub, kind) => {
  const t = { id: ++tid, msg: String(msg ?? ""), sub, kind };
  toasts.set((l) => [...l, t]);
  setTimeout(() => toasts.set((l) => l.filter((x) => x.id !== t.id)), kind === "err" ? 7000 : 4500);
};
export function Toasts() {
  const l = toasts.use();
  return (
    <div className="toasts" id="toasts">
      {l.map((t) => (
        <div key={t.id} className={"toast " + (t.kind || "")}>
          <div className="bar2" />
          <div><div>{t.msg}</div>{t.sub ? <small>{t.sub}</small> : null}</div>
        </div>
      ))}
    </div>
  );
}

/* ---------- copy ---------- */
export function copy(text: string) {
  const ok = () => toast("Copied to clipboard", String(text).length > 60 ? "" : String(text));
  const fallback = () => {
    // navigator.clipboard only exists on HTTPS/localhost; the console is usually plain HTTP on the LAN
    const t = document.createElement("textarea");
    t.value = text; t.setAttribute("readonly", "");
    t.style.cssText = "position:fixed;top:0;left:0;opacity:0;pointer-events:none";
    (document.querySelector(".modal") || document.body).appendChild(t);
    t.focus(); t.select(); t.setSelectionRange(0, t.value.length);
    let done = false;
    try { done = document.execCommand("copy"); } catch { /* ignore */ }
    t.remove();
    done ? ok() : toast("Copy failed", "Select the text and press Ctrl+C", "warn");
  };
  if (navigator.clipboard && window.isSecureContext) navigator.clipboard.writeText(text).then(ok, fallback);
  else fallback();
}
/* a value with a small copy button next to it */
export function Copyable({ v, cls = "" }: { v: any; cls?: string }) {
  return (
    <span className={"cpy " + cls}>
      <code>{String(v ?? "")}</code>
      <button type="button" className="cpybtn" title="Copy" onClick={(e) => { e.stopPropagation(); copy(String(v ?? "")); }}>⧉</button>
    </span>
  );
}

/* ---------- small visual helpers ---------- */
export function Badge({ s, txt }: { s: string; txt?: ReactNode }) {
  return <span className={"badge " + statusClass(s)}><span className="dot" />{txt ?? s}</span>;
}
export function Chips({ items }: { items: (false | null | undefined | [ReactNode, string, string?])[] }) {
  return (
    <div className="chips">
      {items.filter(Boolean).map((it, i) => {
        const [n, l, cls] = it as [ReactNode, string, string?];
        return <span key={i} className={"chip " + (cls || "")}><b>{n}</b> {l}</span>;
      })}
    </div>
  );
}
export function Meter({ v, w }: { v: number; w?: number | string }) {
  return (
    <div className="metric" style={w ? { width: w } : undefined}>
      <div className="bar" style={{ flex: 1 }}><i className={lvl(v)} style={{ width: Math.min(100, v || 0) + "%" }} /></div>
      <b>{Math.round(v || 0)}%</b>
    </div>
  );
}
export function Bars({ w }: { w: any }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {["cpu", "ram", "disk"].map((k) => (
        <div key={k} className="metric">
          <span style={{ width: 26 }}>{k.toUpperCase()}</span>
          <div className="bar" style={{ flex: 1 }}><i className={lvl(w[k])} style={{ width: Math.min(100, w[k] || 0) + "%" }} /></div>
          <b>{Math.round(w[k] || 0)}%</b>
        </div>
      ))}
    </div>
  );
}
export function Sparkline({ data, color, h = 54, w = 560, max = 100 }: { data?: number[]; color: string; h?: number; w?: number; max?: number }) {
  if (!data || data.length < 2) return <div className="chart-empty" style={{ height: h }}>no data yet</div>;
  const mx = max || Math.max(1, ...data);
  const step = w / (data.length - 1);
  const pts = data.map((d, i) => `${(i * step).toFixed(1)},${(h - (Math.min(d, mx) / mx) * h).toFixed(1)}`).join(" ");
  return (
    <svg className="spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: "100%", height: h }}>
      <line className="grid-l" x1="0" y1={h * 0.5} x2={w} y2={h * 0.5} strokeWidth="1" />
      <polygon points={`0,${h} ${pts} ${w},${h}`} fill={color} opacity=".12" />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}
export const Spin = () => <span className="spin" />;
export function Empty({ title, children }: { title: ReactNode; children?: ReactNode }) {
  return <div className="empty"><h3>{title}</h3>{children}</div>;
}

/* ---------- floating row menus ---------- */
type MenuState = { rect: DOMRect; content: ReactNode } | null;
const menu = createStore<MenuState>(null);
export function showMenu(e: React.MouseEvent, content: ReactNode) {
  e.stopPropagation();
  menu.set({ rect: (e.currentTarget as HTMLElement).getBoundingClientRect(), content });
}
export const closeMenu = () => menu.set(null);
export function MenuRoot() {
  const m = menu.use();
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  useLayoutEffect(() => {
    if (!m || !ref.current) { setPos(null); return; }
    const el = ref.current;
    setPos({
      top: Math.max(8, Math.min(m.rect.bottom + 4, window.innerHeight - el.offsetHeight - 10)),
      left: Math.max(8, m.rect.right - el.offsetWidth),
    });
    // any click (including one on a menu item, after its handler) closes the menu
    const close = () => setTimeout(closeMenu, 0);
    const t = setTimeout(() => document.addEventListener("click", close, true), 0);
    return () => { clearTimeout(t); document.removeEventListener("click", close, true); };
  }, [m]);
  if (!m) return null;
  // right/margin reset: .menu in app.css is also the in-place dropdown (right:0; top:100%),
  // which would stretch this fixed-position menu across the whole window
  return (
    <div ref={ref} className="menu" id="floatmenu"
      style={{ position: "fixed", top: pos?.top ?? -9999, left: pos?.left ?? -9999, right: "auto", marginTop: 0, width: "max-content", maxWidth: "min(360px, calc(100vw - 16px))" }}>
      {m.content}
    </div>
  );
}

/* ---------- error boundary so one broken page doesn't blank the console ---------- */
export class PageBoundary extends Component<{ children: ReactNode; onHome: () => void }, { err: Error | null }> {
  state = { err: null as Error | null };
  static getDerivedStateFromError(err: Error) { return { err }; }
  componentDidCatch(err: Error) { console.error(err); }
  render() {
    if (this.state.err)
      return (
        <div className="empty">
          <h3>Something went wrong drawing this page</h3>
          <p className="mono">{this.state.err.message}</p>
          <button className="btn" onClick={() => { this.setState({ err: null }); this.props.onHome(); }}>Back to overview</button>
        </div>
      );
    return this.props.children;
  }
}
